import React from 'react';
import { Link } from 'react-router-dom';

const HomePage = () => {
  const tools = [
    {
      id: 'image-editor',
      title: 'Image Editor',
      description: 'Edit and enhance your images with professional tools',
      icon: (
        <svg className="w-12 h-12 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      ),
      path: '/image-editor',
      available: true
    },
    {
      id: 'svg-tools',
      title: 'SVG Tools',
      description: 'Create and edit scalable vector graphics',
      icon: (
        <svg className="w-12 h-12 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      ),
      path: '/svg-tools',
      available: false
    },
    {
      id: 'batch-resizer',
      title: 'Batch Resizer',
      description: 'Resize multiple images at once with custom dimensions',
      icon: (
        <svg className="w-12 h-12 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
        </svg>
      ),
      path: '/batch-resizer',
      available: true
    }
  ];

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      <div className="container mx-auto px-6 py-12">
        {/* Header */}
        <div className="text-center mb-16">
          <h1 className="text-5xl font-bold mb-4 bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
            Image Tools Suite
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Professional image editing and processing tools for all your creative needs. 
            Choose from our collection of powerful, browser-based image utilities.
          </p>
        </div>

        {/* Tool Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {tools.map((tool) => (
            <div key={tool.id} className="relative group">
              {tool.available ? (
                <Link to={tool.path} className="block">
                  <div className="bg-gray-800 rounded-xl p-8 border border-gray-700 hover:border-gray-600 transition-all duration-300 hover:transform hover:scale-105 hover:shadow-2xl">
                    <div className="flex flex-col items-center text-center">
                      <div className="mb-6 p-4 bg-gray-700 rounded-full group-hover:bg-gray-600 transition-colors">
                        {tool.icon}
                      </div>
                      <h3 className="text-2xl font-bold mb-3 text-white group-hover:text-blue-400 transition-colors">
                        {tool.title}
                      </h3>
                      <p className="text-gray-400 group-hover:text-gray-300 transition-colors">
                        {tool.description}
                      </p>
                      <div className="mt-6 inline-flex items-center text-blue-400 group-hover:text-blue-300">
                        <span className="mr-2">Get Started</span>
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </div>
                    </div>
                  </div>
                </Link>
              ) : (
                <div className="bg-gray-800 rounded-xl p-8 border border-gray-700 opacity-50 cursor-not-allowed">
                  <div className="flex flex-col items-center text-center">
                    <div className="mb-6 p-4 bg-gray-700 rounded-full">
                      {tool.icon}
                    </div>
                    <h3 className="text-2xl font-bold mb-3 text-gray-500">
                      {tool.title}
                    </h3>
                    <p className="text-gray-500 mb-4">
                      {tool.description}
                    </p>
                    <span className="text-sm text-yellow-400 bg-yellow-400/10 px-3 py-1 rounded-full">
                      Coming Soon
                    </span>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Features Section */}
        <div className="mt-20 text-center">
          <h2 className="text-3xl font-bold mb-8 text-white">Why Choose Our Tools?</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
              <div className="w-12 h-12 bg-blue-500/10 rounded-lg flex items-center justify-center mb-4 mx-auto">
                <svg className="w-6 h-6 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold mb-2 text-white">Lightning Fast</h3>
              <p className="text-gray-400">All processing happens locally in your browser for maximum speed and privacy.</p>
            </div>
            <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
              <div className="w-12 h-12 bg-green-500/10 rounded-lg flex items-center justify-center mb-4 mx-auto">
                <svg className="w-6 h-6 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold mb-2 text-white">Secure & Private</h3>
              <p className="text-gray-400">Your images never leave your device. Complete privacy guaranteed.</p>
            </div>
            <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
              <div className="w-12 h-12 bg-purple-500/10 rounded-lg flex items-center justify-center mb-4 mx-auto">
                <svg className="w-6 h-6 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold mb-2 text-white">Easy to Use</h3>
              <p className="text-gray-400">Intuitive interfaces designed for both beginners and professionals.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HomePage; 